import React, { useEffect, useState } from 'react';
import { createStructuredSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { KMF_COVER_CATEGORIES } from '@om/redux/actions/kaMetlhaFuneralCover/types';

import {
    OmFormInputFieldWrapper,
    OmCheckBox,
    OmSlider,
    OmLoader,
    OmSideDrawer,
    OmEfpAddCoverDrawer,
    OmButton,
    OmFormDropdownFieldWrapper,
} from '@om/component-library-react';

import { DEFAULT_VALIDATION_DELAY } from '../../../config';
import {
    getCoverType,
    getDisplayParentCoverOptions,
    getEditQuoteDetails,
    getHighestCoverAmount,
    getLivesCoveredCategoryOptions,
    getMainCoverAmount,
    getMonthlyPremiumQuote,
    getQuoteForm,
} from '@om/redux/selectors/kaMetlhaFuneralCover/quote';
import {
    addToQuote,
    setQuoteDetailsStateValue,
    setQuoteDetailsValue,
    setQuoteDrawerValue,
    synchFormValue,
} from '@om/redux/actions/kaMetlhaFuneralCover/quote';
import { getQuoteRefernceData } from '@om/redux/selectors/kaMetlhaFuneralCover/referenceData';
import { FIELD_STATUS } from '@om/redux/reducers/status';

const FormContent = () => {
    const {
        coverType,
        formFields,
        quoteRefrenceData,
        displayParentsandInLaws,
        displayCoverCategories,
        calculatedPremium,
        mainCoverAmount,
        highestCoverAmount,
        editQuoteDetails,
    } = useSelector(
        createStructuredSelector({
            coverType: getCoverType,
            formFields: getQuoteForm,
            quoteRefrenceData: getQuoteRefernceData,
            displayParentsandInLaws: getDisplayParentCoverOptions,
            displayCoverCategories: getLivesCoveredCategoryOptions(),
            calculatedPremium: getMonthlyPremiumQuote,
            mainCoverAmount: getMainCoverAmount,
            highestCoverAmount: getHighestCoverAmount,
            editQuoteDetails: getEditQuoteDetails,
        })
    );

    const { displayExtendedFamilyMembers, quoteDisplayCoverCategories } = quoteRefrenceData;
    const { dateOfBirth, age, relationship, coverAmount, monthlyPremium, selectedCoverCategory, title } = formFields;
    const displayCoverMembers = selectedCoverCategory?.value?.startsWith(KMF_COVER_CATEGORIES.EXTENDED_FAMILY)
        ? displayExtendedFamilyMembers : selectedCoverCategory?.value?.startsWith(KMF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS) ? displayParentsandInLaws : [];
    const actionHandler = (fieldType, value, displayValue = null) => {
        dispatch(setQuoteDetailsValue({ fieldType, value, displayValue }));
    };

    const dispatch = useDispatch();
    useEffect(() => {
        calculatedPremium && actionHandler('monthlyPremium', calculatedPremium);
    }, [calculatedPremium]);

    useEffect(() => {
        !coverAmount.value &&
            coverType === KMF_COVER_CATEGORIES.LIVES_COVERED_SIDE_DRAWER &&
            actionHandler('coverAmount', 5000);
    }, [coverType]);

    const coverAmountOptions = [5000, 10000, 15000, 20000, 25000, 30000].filter((val) => val <= +mainCoverAmount / 2);
    return (
        <>
            <OmSideDrawer
                open={coverType === KMF_COVER_CATEGORIES.LIVES_COVERED_SIDE_DRAWER}
                onOnSideDrawerClosed={() => {
                    dispatch(setQuoteDrawerValue({ fieldType: 'coverType', value: null }));
                    dispatch(
                        setQuoteDetailsStateValue({
                            fieldType: 'editQuoteDetails',
                            value: { id: null, category: null },
                        })
                    );
                    // dispatch(setSideDrawerClosed());
                }}
            >
                <div slot="side-drawer-content">
                    <OmEfpAddCoverDrawer>
                        <div className="progressive-disclosure-form-wrapper">
                            <h6 className="no-top-margin medium">Who would you like to cover?</h6>
                            <OmFormDropdownFieldWrapper
                                align="left"
                                placeholder="Select a cover category*"
                                placeholderAsLabel="false"
                                formId="addLivesCovered"
                                size="full"
                                name="coverCategory"
                                required
                                disabled={!!title.value}
                                useNativeMobileDropdown
                                value={selectedCoverCategory && selectedCoverCategory?.value}
                                state={selectedCoverCategory && selectedCoverCategory.status}
                                errorMessage={selectedCoverCategory && selectedCoverCategory.error}
                                options={JSON.stringify(
                                    displayCoverCategories &&
                                        displayCoverCategories.map((category) => ({
                                            value: category.code,
                                            label: category.description,
                                            disabled: category.disabled,
                                        }))
                                )}
                                onOnSelect={(event) => {
                                    actionHandler(
                                        'selectedCoverCategory',
                                        event.detail.value.startsWith('My Extended')
                                            ? KMF_COVER_CATEGORIES.EXTENDED_FAMILY
                                            : event.detail.value === 'Child'
                                                ? KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY
                                                : KMF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS
                                    );
                                    relationship.value && actionHandler('relationship', null);
                                    dateOfBirth.value && actionHandler('dateOfBirth', dateOfBirth.value);
                                }}
                            />
                            <OmFormDropdownFieldWrapper
                                align="left"
                                label=" "
                                placeholder="What's your relationship?*"
                                placeholderAsLabel="false"
                                formId="addInLaws"
                                size="full"
                                name={`Relationship${relationship.value ?? ''}`}
                                useNativeMobileDropdown
                                required
                                disabled={!!title.value}
                                key={relationship.value}
                                value={relationship?.value}
                                state={relationship?.status}
                                errorMessage={relationship?.error}
                                options={JSON.stringify(
                                    displayCoverMembers &&
                                        displayCoverMembers.map((coverCategory) => ({
                                            value: coverCategory.code,
                                            label: coverCategory.description,
                                            disabled: coverCategory.disabled,
                                        }))
                                )}
                                onOnSelect={(event) => actionHandler('relationship', event.detail.value)}
                            />

                            <OmFormInputFieldWrapper
                                isSensitive
                                required
                                labelAlwaysShow
                                id="ageField"
                                mask="date"
                                size="full"
                                formId="addLivesCovered"
                                name="Age"
                                align="left"
                                label=" "
                                placeholder={'DD/MM/YYYY*'}
                                hintText={'Format : dd/mm/yyyy'}
                                validationDelay={DEFAULT_VALIDATION_DELAY}
                                value={dateOfBirth?.value}
                                state={age && age?.status}
                                errorMessage={dateOfBirth?.error || age?.error}
                                onPersist={(event) => actionHandler('dateOfBirth', event.detail)}
                            />
                            {dateOfBirth?.status === FIELD_STATUS.VALID && (
                                <>
                                    <h6 className="no-bottom-margin">Select cover amount:</h6>
                                    <OmSlider
                                        key="livescovered"
                                        id="lmskskm"
                                        stepString={JSON.stringify(coverAmountOptions)}
                                        minLabel={`P${coverAmountOptions[0]}`}
                                        maxLabel={`P${coverAmountOptions.at(-1)}`}
                                        value={coverAmount?.value ?? 5000}
                                        valuePrefix={'P'}
                                        onSliderChange={(event) => actionHandler('coverAmount', event.detail)}
                                    />

                                    <div
                                        className="cover-label-wrapper medium-margin-top"
                                        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}
                                    >
                                        <span className="cover-label" style={{ width: '50%' }}>
                                            <h5 className="no-bottom-margin no-top-margin medium">
                                                P {coverAmount?.value}
                                            </h5>
                                            <h6 className="small medium no-top-margin">COVER AMOUNT</h6>
                                        </span>

                                        <span className="cover-label" style={{ width: '50%', textAlign: 'right' }}>
                                            <h5 className="no-bottom-margin no-top-margin medium">
                                                P {monthlyPremium?.value?.toFixed(2)}
                                            </h5>
                                            <h6 className="small medium no-top-margin base-margin-bottom">
                                                MONTHLY PREMIUM
                                            </h6>
                                            {/* <h5 className="no-bottom-margin no-top-margin medium">
                                                P {+monthlyPremium.value * 12}
                                            </h5>
                                            <h6 className="small medium no-top-margin">ANNUALLY PREMIUM</h6> */}
                                        </span>

                                        <span className="no-margin"></span>
                                    </div>
                                </>
                            )}
                            <div slot="modal-content"></div>

                            <div className="action-buttons">
                                {editQuoteDetails.id ? (
                                    <OmButton
                                        type="text"
                                        size="small"
                                        text="CANCEL"
                                        onClick={() => {
                                            dispatch(setQuoteDrawerValue({ fieldType: 'coverType', value: null }));
                                            dispatch(
                                                setQuoteDetailsStateValue({
                                                    fieldType: 'editQuoteDetails',
                                                    value: { id: null, category: null },
                                                })
                                            );
                                        }}
                                    />
                                ) : (
                                    <OmButton
                                        type="text"
                                        size="small"
                                        text="CLEAR ALL"
                                        onClick={() => {
                                            dispatch(
                                                setQuoteDrawerValue({
                                                    fieldType: 'coverType',
                                                    value: KMF_COVER_CATEGORIES.LIVES_COVERED_SIDE_DRAWER,
                                                })
                                            );
                                            actionHandler('coverAmount', 5000);
                                        }}
                                    />
                                )}
                                <OmButton
                                    type="primary"
                                    size="small"
                                    text={'ADD TO QUOTE'}
                                    onClick={() => {
                                        coverAmount?.value > highestCoverAmount &&
                                            dispatch(
                                                setQuoteDetailsStateValue({
                                                    fieldType: 'highestCoverAmount',
                                                    value: coverAmount?.value,
                                                })
                                            );
                                        dispatch(
                                            addToQuote({
                                                coverType:
                                                    selectedCoverCategory?.value ===
                                                    KMF_COVER_CATEGORIES.EXTENDED_FAMILY
                                                        ? KMF_COVER_CATEGORIES.EXTENDED_FAMILY
                                                        : selectedCoverCategory?.value === 'Child'
                                                            ? KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY
                                                            : KMF_COVER_CATEGORIES.PARENTS_AND_IN_LAWS,
                                                relation: relationship ? relationship?.value : 'child',
                                            })
                                        );
                                    }}
                                />
                            </div>
                        </div>
                    </OmEfpAddCoverDrawer>
                </div>
            </OmSideDrawer>
        </>
    );
};

const LivesCoveredSideDrawer = () => {
    // if (applicationReferenceDataValid && quoteReferenceDataValid) {
    return <FormContent />;
    // }

    // return (
    //     <div>
    //         {loadingText(directFamilyDrawerOpen)}
    //     </div>
    // );
};

export default LivesCoveredSideDrawer;
